<template>
  <section class="section -less-pad" v-if="$page">
    <h1 class="section__header">{{ $page.header.title }}</h1>
    <p class="soumission__description">{{ $page.header.description }}</p>

    <h2 class="soumission__subtitle">{{ $page.quote.title }}</h2>
    <p class="soumission__description">{{ $page.quote.description }}</p>

    <transition name="fade" mode="out-in">
      <div v-if="sent" class="soumission__confirm">
        <div class="soumission__confirm-left">
          <div class="soumission__confirm-check">
            <Icon id="checkmark" />
          </div>

          <h3 class="soumission__confirm-title">
            {{ $t('quote.confirm') }}
          </h3>

          <button class="button -right" @click="reload">
            {{ $t('quote.submitAnother') }}

            <div class="button__icon">
              <Icon id="arrow" />
            </div>
          </button>
        </div>

        <div class="soumission__confirm-right">
          <b>{{ $t('quote.questionsTitle') }}</b>
          <p>{{ $t('quote.questionsDescription') }}</p>

          <a :href="footer.gmap_link" target="_blank" class="footer__links-list-item">
            {{ footer.address }}
          </a>

          <div class="footer__links-list-item">
            <p>
              <a :href="`tel:${footer.phone_tollfree}`">
                {{ $t('footer.toll_free') }}: {{ footer.phone_tollfree }}
              </a>
            </p>

            <p>
              <a :href="`tel:${footer.phone}`"> {{ $t('footer.phone') }}: {{ footer.phone }} </a>
            </p>
          </div>

          <a :href="`mailto:${footer.email}`" class="footer__links-list-item -email">
            {{ footer.email }}
            <Icon id="arrow" />
          </a>
        </div>
      </div>

      <div v-else-if="sending">
        <Loader style="min-height: 30vh" />
      </div>

      <form v-else @submit.prevent="submit" class="soumission__form">
        <RawHtml :html="notice" class="soumission__notice" />

        <fieldset class="soumission__form-row">
          <label class="input" ref="form.destination_id">
            <span class="input__label" :class="{ '-error': errorFields.destination_id }">
              {{ $t('quote.destination') }}
              <span class="input__required">*</span>
            </span>

            <div class="soumission__select">
              <select v-model="form.destination_id">
                <option :value="0">
                  {{ $t('quote.selectDestination') }}
                </option>

                <option
                  v-for="(destination, key) in destinations"
                  :key="`destination-${key}`"
                  :value="destination.id"
                >
                  {{ destination.name }}
                </option>
              </select>
            </div>
          </label>

          <div class="soumission__form-or">
            {{ $t('quote.orTxt') }}
          </div>

          <label class="input" ref="form.destination_other">
            <input
              type="text"
              v-model="form.destination_other"
              :placeholder="$t('quote.otherDestinationPH')"
              class="input__input"
            />
          </label>
        </fieldset>

        <FieldError :errors="validationErrors.destination_id" />
        <FieldError :errors="validationErrors.destination_other" />

        <fieldset class="soumission__form-row">
          <label class="input" ref="form.service_center_id">
            <span class="input__label" :class="{ '-error': errorFields.service_center_id }">
              {{ $t('quote.servicesCenter') }}
            </span>

            <div class="soumission__select">
              <select v-model="form.service_center_id">
                <option :value="0">
                  {{ $t('quote.selectSC') }}
                </option>

                <option
                  v-for="(service, key) in services"
                  :key="`service-${key}`"
                  :value="service.id"
                >
                  {{ service.name }}
                </option>
              </select>
            </div>
          </label>

          <label class="input" ref="form.school_name">
            <span class="input__label" :class="{ '-error': errorFields.school_name }">
              {{ $t('quote.school') }}
            </span>

            <input type="text" class="input__input" v-model="form.school_name" />
          </label>
        </fieldset>

        <FieldError :errors="validationErrors.service_center_id" />
        <FieldError :errors="validationErrors.school_name" />

        <fieldset class="soumission__form-row">
          <label class="input" ref="form.organizer_name">
            <span class="input__label" :class="{ '-error': errorFields.organizer_name }">
              {{ $t('quote.organizerName') }}
              <span class="input__required">*</span>
            </span>

            <input type="text" class="input__input" v-model="form.organizer_name" />
          </label>

          <label class="input" ref="form.organizer_email">
            <span class="input__label" :class="{ '-error': errorFields.organizer_email }">
              {{ $t('quote.organizerEmail') }}
              <span class="input__required">*</span>
            </span>

            <input type="email" class="input__input" v-model="form.organizer_email" />
          </label>

          <label class="input" ref="form.organizer_phone">
            <span class="input__label" :class="{ '-error': errorFields.organizer_phone }">
              {{ $t('quote.organizerPhone') }}
              <span class="input__required">*</span>
            </span>

            <input type="text" class="input__input" v-model="form.organizer_phone" />
          </label>
        </fieldset>

        <FieldError :errors="validationErrors.organizer_name" />
        <FieldError :errors="validationErrors.organizer_email" />
        <FieldError :errors="validationErrors.organizer_phone" />

        <fieldset class="soumission__form-row">
          <label class="input" ref="form.planned_departure">
            <span class="input__label" :class="{ '-error': errorFields.planned_departure }">
              {{ $t('quote.plannedDeparture') }}
            </span>

            <input type="datetime-local" class="input__input" v-model="form.planned_departure" />
          </label>

          <label class="input" ref="form.planned_return">
            <span class="input__label" :class="{ '-error': errorFields.planned_return }">
              {{ $t('quote.plannedReturn') }}
            </span>

            <input type="datetime-local" class="input__input" v-model="form.planned_return" />
          </label>
        </fieldset>

        <FieldError :errors="validationErrors.planned_departure" />
        <FieldError :errors="validationErrors.planned_return" />

        <fieldset class="soumission__form-row">
          <label class="input" ref="form.students_number">
            <span class="input__label" :class="{ '-error': errorFields.students_number }">
              {{ $t('quote.studentsNumber') }}
            </span>

            <input type="number" class="input__input" v-model.number="form.students_number" />
          </label>

          <label class="input" ref="form.chaperons_number">
            <span class="input__label" :class="{ '-error': errorFields.chaperons_number }">
              {{ $t('quote.chaperonsNumber') }}
            </span>

            <input type="number" class="input__input" v-model.number="form.chaperons_number" />
          </label>
        </fieldset>

        <FieldError :errors="validationErrors.students_number" />
        <FieldError :errors="validationErrors.chaperons_number" />

        <fieldset class="soumission__form-row">
          <label class="input" ref="form.with_tour_guide">
            <span class="input__label">
              {{ $t('quote.withTourGuide') }}
            </span>

            <div class="input__radio" @click="form.with_tour_guide = true">
              <div class="input__radio-circle" :class="{ '-checked': form.with_tour_guide }">
                <Icon id="checkmark" v-if="form.with_tour_guide" />
              </div>

              <div class="input__radio-label">
                {{ $t('quote.yes') }}
              </div>
            </div>
          </label>

          <label class="input" @click="form.with_tour_guide = false">
            <div class="input__radio">
              <div
                class="input__radio-circle"
                :class="{ '-checked': form.with_tour_guide === false }"
              >
                <Icon id="checkmark" v-if="form.with_tour_guide === false" />
              </div>

              <div class="input__radio-label">
                {{ $t('quote.no') }}
              </div>
            </div>
          </label>
        </fieldset>

        <FieldError :errors="validationErrors.with_tour_guide" />

        <fieldset class="soumission__form-row">
          <label
            class="input"
            v-for="(insurance, key, index) in insurances"
            :key="key"
            :class="{
              '-half': index > 1,
              '-end': index === Object.values(insurances).length - 1,
            }"
            @click="form.included_insurance = key"
            ref="form.included_insurance"
          >
            <span class="input__label" v-if="index === 0">
              {{ $t('quote.insurances') }}
            </span>

            <div class="input__radio">
              <div
                class="input__radio-circle"
                :class="{ '-checked': form.included_insurance === key }"
              >
                <Icon id="checkmark" v-if="form.included_insurance === key" />
              </div>

              <div class="input__radio-label">
                {{ insurance }}
              </div>
            </div>
          </label>
        </fieldset>

        <FieldError :errors="validationErrors.included_insurance" />

        <label class="textarea" ref="form.description">
          <div class="textarea__label">
            {{ $t('quote.descriptionTitle') }}
          </div>

          <p>{{ $t('quote.descriptionHelp') }}</p>

          <textarea class="textarea__input" v-model="form.description" />

          <FieldError :errors="validationErrors.description" />
        </label>

        <label class="file" ref="form.upload">
          <div class="file__row">
            <button class="button -right file__input" type="button" @click="openFile">
              {{ $t('quote.chooseFile') }}

              <input type="file" class="file__input-input" ref="file" @change="handleFile" />

              <div class="button__icon -light">
                <Icon id="trombonne" />
              </div>
            </button>

            <div class="file__info">
              <transition name="fade" mode="out-in">
                <span v-if="form.upload">
                  {{ form.upload.name }}
                </span>

                <span v-else>
                  {{ $t('quote.noFile') }}
                </span>
              </transition>
            </div>
          </div>

          <FieldError :errors="validationErrors.upload" />
        </label>

        <fieldset class="soumission__form-row">
          <label
            class="input -half"
            v-for="(name, key, index) in sources"
            :key="key"
            :class="{ '-end': index > 1 }"
            @click="addSource(key)"
            ref="form.client_source"
          >
            <span
              v-if="index === 0"
              class="input__label"
              :class="{ '-error': errorFields.client_source }"
            >
              {{ $t('quote.clientSource') }}
            </span>

            <div class="input__radio">
              <div
                class="input__radio-circle -box"
                :class="{
                  '-checked': form.client_source.includes(key),
                }"
              >
                <Icon id="checkmark" v-if="form.client_source.includes(key)" />
              </div>

              <div class="input__radio-label">
                {{ name }}
              </div>
            </div>
          </label>
        </fieldset>

        <FieldError :errors="validationErrors.client_source" />

        <div class="flex-row -end soumission__form-submit">
          <button class="button -right" type="submit">
            {{ $t('quote.submit') }}

            <div class="button__icon">
              <Icon id="arrow" />
            </div>
          </button>
        </div>

        <div class="flex-row -end soumission__form-error">
          <transition name="fade" appear>
            <div v-if="error">
              {{ error }}
            </div>
          </transition>
        </div>
      </form>
    </transition>
  </section>

  <PageBuilder :sections="$page.sections" v-if="$page" />
</template>

<script>
import Loader from '@/components/blocks/Loader.vue'
import FieldError from '@/components/global/FieldError.vue'
import Icon from '@/components/global/Icon.vue'
import RawHtml from '@/components/global/RawHtml.vue'
import PageBuilder from '@/components/PageBuilder.vue'
import http from '@/extensions/http'
import { setMeta } from '@/helpers/metaHelpers'

export default {
  name: 'Soumission',

  components: {
    FieldError,
    Icon,
    Loader,
    PageBuilder,
    RawHtml,
  },

  beetPage: 'quote',

  data() {
    return {
      sending: false,
      sent: false,
      error: null,
      validationErrors: {},
      errorFields: {},

      form: this.emptyForm(),
    }
  },

  watch: {
    $page(to) {
      setMeta(to.seo)
    },

    form: {
      deep: true,

      handler() {
        this.errorFields = {}
        this.validationErrors = {}
        this.error = null
      },
    },
  },

  beforeMount() {
    if (this.$page) {
      setMeta(this.$page.seo)
    }
  },

  methods: {
    emptyForm() {
      return {
        destination_id: 0,
        destination_other: '',
        service_center_id: 0,
        school_name: '',
        organizer_name: '',
        organizer_email: '',
        organizer_phone: '',
        planned_departure: '',
        planned_return: '',
        students_number: null,
        chaperons_number: null,
        with_tour_guide: true,
        included_insurance: 'none',
        description: '',
        client_source: [],
        upload: null,
      }
    },

    reload() {
      this.form = this.emptyForm()
      this.validationErrors = {}
      this.errorFields = {}
      this.error = null
      this.sent = false
      this.sending = false
    },

    addSource(source) {
      if (this.form.client_source.includes(source)) {
        this.form.client_source = this.form.client_source.filter((item) => item !== source)

        return
      }

      this.form.client_source.push(source)
    },

    openFile() {
      this.$refs.file.click()
    },

    handleFile() {
      this.form.upload = this.$refs.file.files[0] ?? null
    },

    submit() {
      if (this.sent || this.sending) {
        return
      }

      /*
       * Keep the client-side file check so a known-too-large file
       * doesn't need to be uploaded before displaying the error.
       */
      if (this.form.upload && this.form.upload.size > 5242880) {
        this.validationErrors = {
          upload: [
            this.$t('quote.fileTooBigError', {
              size: '5mb',
            }),
          ],
        }

        this.errorFields = {
          upload: true,
        }

        this.scrollTo(this.$refs['form.upload'])

        return
      }

      this.sending = true
      this.validationErrors = {}
      this.errorFields = {}
      this.error = null

      const formData = new FormData()

      Object.entries(this.form).forEach(([key, value]) => {
        if ((key === 'destination_id' || key === 'service_center_id') && Number(value) === 0) {
          return
        }

        if (key === 'client_source') {
          value.forEach((source) => {
            formData.append('client_source[]', source)
          })

          return
        }

        if (key === 'with_tour_guide') {
          formData.append(key, value ? '1' : '0')

          return
        }

        if (value === null || value === '') {
          return
        }

        formData.append(key, value)
      })

      http
        .post('/quotes', formData)
        .then(() => {
          window.scrollTo({
            top: 0,
            behavior: 'smooth',
          })

          this.sent = true
        })
        .catch((err) => {
          if (err.response?.status === 422) {
            this.handleValidationErrors(err.response.data.errors ?? {})

            return
          }

          if (err.response?.status === 413) {
            this.validationErrors = {
              upload: [
                this.$t('quote.fileTooBigError', {
                  size: '5mb',
                }),
              ],
            }

            this.errorFields = {
              upload: true,
            }

            this.scrollTo(this.$refs['form.upload'])

            return
          }

          this.error = err.response?.data?.message ?? err.message ?? 'An error occurred.'
        })
        .finally(() => {
          this.sending = false
        })
    },

    handleValidationErrors(errors) {
      const validationErrors = {}

      /*
       * Laravel can return nested keys such as:
       *
       * client_source.0
       * client_source.1
       *
       * The UI has one error area for client_source, so group
       * nested errors under their root field.
       */
      Object.entries(errors).forEach(([key, messages]) => {
        const field = key.split('.')[0]

        validationErrors[field] = [...(validationErrors[field] ?? []), ...messages]
      })

      this.validationErrors = validationErrors

      const fields = Object.keys(validationErrors)

      this.errorFields = fields.reduce((fieldsWithErrors, field) => {
        fieldsWithErrors[field] = true

        return fieldsWithErrors
      }, {})

      const firstField = fields[0]

      if (!firstField) {
        return
      }

      this.scrollTo(this.$refs[`form.${firstField}`])
    },

    scrollTo(ref) {
      /*
       * Refs used inside v-for are arrays in Vue.
       */
      const element = Array.isArray(ref) ? ref[0] : ref

      if (!element) {
        return
      }

      element.scrollIntoView({
        block: 'start',
        behavior: 'smooth',
      })

      const y = element.getBoundingClientRect().top + window.scrollY - 175

      window.scrollTo({
        top: y,
        behavior: 'smooth',
      })
    },
  },

  computed: {
    footer() {
      return this.$beet.options.footer || {}
    },

    destinations() {
      return Object.values(
        Object.values(this.$beet.destinations).reduce((acc, obj) => {
          acc[obj.id] = {
            name: obj.name,
            id: obj.id,
          }

          return acc
        }, {}),
      ).toSorted((a, b) => {
        const nameA = a.name.toLowerCase().normalize('NFD')
        const nameB = b.name.toLowerCase().normalize('NFD')

        if (nameA < nameB) {
          return -1
        }

        if (nameA > nameB) {
          return 1
        }

        return 0
      })
    },

    services() {
      if (!this.$beet.service_centers) {
        return []
      }

      const services = Object.keys(this.$beet.service_centers).map((key) => ({
        id: key,
        name: this.$beet.service_centers[key],
      }))

      services.sort((a, b) => {
        const nameA = a.name.toLowerCase().normalize('NFD')
        const nameB = b.name.toLowerCase().normalize('NFD')

        if (nameA < nameB) {
          return -1
        }

        if (nameA > nameB) {
          return 1
        }

        return 0
      })

      return services
    },

    insurances() {
      return this.$beet.quote_insurances
    },

    sources() {
      return this.$beet.quote_sources
    },

    notice() {
      return this.$t('quote.required_fields').replace('(*)', '<span>*</span>')
    },
  },
}
</script>
